import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import MapIcon from '@mui/icons-material/Map';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import Collapse from '@mui/material/Collapse';
import { Divider, IconButton, Typography } from '@mui/material';
import logo from './assets/PEO_InformalLogo_Color.png';
import Grid from '@mui/material/Grid';
import {
  GridView,
  Menu,
  Message,
  ShoppingCart,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { applications, roles } from './utils/variables';
import { getUnreadCount } from './endpoints/messagesTs';
import { useApiGet } from './utils/httpClientTs';
import { useNavigate } from 'react-router-dom';
import { canViewTransferRequests } from './utils/permissions';
import GlobalSearch from './Components/GlobalSearch';
import { config } from './utils/config';

interface MenuDrawerProps {
  drawerWidth: number;
  open: boolean;
  handleUpdateDrawer: () => void;
  isMobile: boolean;
}

interface UnreadCountData {
  count: number;
}

export default function MenuDrawer({
  drawerWidth,
  open,
  handleUpdateDrawer,
  isMobile,
}: MenuDrawerProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [openChapters, setOpenChapters] = useState<boolean>(true);
  const [projectsOpen, setProjectsOpen] = useState<boolean>(false);
  const [resourcesOpen, setResourcesOpen] = useState<boolean>(false);
  const [shopsOpen, setShopsOpen] = useState<boolean>(false);
  const [annualReportsOpen, setAnnualReportsOpen] = useState<boolean>(false);
  const [administrationOpen, setAdministrationOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data: unreadCountData } = useApiGet<UnreadCountData>(
    ['unread-count'],
    getUnreadCount,
    {
      enabled: open && localStorage.getItem('user_id') !== null,
      retry: 1,
      refetchInterval: 60 * 1000, // The data will be refetched every 1 minute
    }
  );

  // Listen for website search events
  useEffect(() => {
    const handleWebsiteSearch = (event: CustomEvent) => {
      const query = event.detail.query;
      setSearchQuery(query);

      if (!query) {
        return;
      }

      // Define all menu items with their text and parent sections
      const menuItems = [
        // Main items
        { key: 'dashboard', text: 'Dashboard', parent: null },
        { key: 'annual-reports', text: 'Annual Reports', parent: null },
        { key: 'chapters-groups', text: 'Chapters / Groups', parent: null },
        { key: 'resources', text: 'Resources', parent: null },
        { key: 'shops', text: 'Shops', parent: null },
        { key: 'philanthropy', text: 'Projects / Foundation', parent: null },
        { key: 'conversations', text: 'My Messages', parent: null },
        { key: 'profile', text: 'My Profile', parent: null },
        { key: 'logout', text: 'Logout', parent: null },

        // Annual Reports submenu
        {
          key: 'ar-dashboard',
          text: 'Annual Reports Dashboard',
          parent: 'annual-reports',
        },
        {
          key: 'ar-prework',
          text: 'Prework',
          parent: 'annual-reports',
        },
        {
          key: 'ar-roster-dues',
          text: 'Roster & Dues',
          parent: 'annual-reports',
        },
        {
          key: 'ar-irs-forms',
          text: 'IRS Forms',
          parent: 'annual-reports',
        },
        {
          key: 'ar-resources',
          text: 'Resources',
          parent: 'annual-reports',
        },
        {
          key: 'ar-training',
          text: 'Training',
          parent: 'annual-reports',
        },

        // Chapters / Groups submenu
        {
          key: 'local-chapters',
          text: 'Local Chapters',
          parent: 'chapters-groups',
        },
        {
          key: 'spd-chapters',
          text: 'S/P/D Chapters',
          parent: 'chapters-groups',
        },
        {
          key: 'reciprocities',
          text: 'International',
          parent: 'chapters-groups',
        },
        {
          key: 'member-search',
          text: 'Member Search',
          parent: 'chapters-groups',
        },
        {
          key: 'introductions',
          text: 'Non-Member Intro',
          parent: 'chapters-groups',
        },

        // Resources submenu
        { key: 'peo-record', text: 'P.E.O. Record', parent: 'resources' },
        { key: 'bnb', text: 'Bed and Breakfast', parent: 'resources' },
        { key: 'tlc', text: 'TLC', parent: 'resources' },
        { key: 'transfer-list', text: 'Transfer List', parent: 'resources' },
        { key: 'library', text: 'Library', parent: 'resources' },
        { key: 'map', text: 'P.E.O. Map', parent: 'resources' },
        { key: 'training', text: 'Training', parent: 'resources' },
        {
          key: 'leadership-development',
          text: 'Leadership Development',
          parent: 'resources',
        },
        { key: 'newsfeed', text: 'Newsfeed', parent: 'resources' },
        { key: 'reports', text: 'Reports', parent: 'resources' },
        { key: 'messages', text: 'Messaging', parent: 'resources' },
        { key: 'feedback', text: 'Feedback', parent: 'resources' },

        // Shops submenu
        {
          key: 'marketplace',
          text: 'Fundraising Marketplace',
          parent: 'shops',
        },
        { key: 'jewelry', text: 'Jewelry (Herff-Jones)', parent: 'shops' },
        { key: 'chapter-supplies', text: 'Chapter Supplies', parent: 'shops' },
        { key: 'emblem-order', text: 'Emblem Order', parent: 'shops' },

        // Projects / Foundation submenu
        {
          key: 'elf',
          text: 'ELF (Educational Loan Fund)',
          parent: 'philanthropy',
        },
        {
          key: 'ips',
          text: 'IPS (International Peace Scholarship)',
          parent: 'philanthropy',
        },
        {
          key: 'pce',
          text: 'PCE (Program for Continuing Education)',
          parent: 'philanthropy',
        },
        {
          key: 'psa',
          text: 'PSA (P.E.O. Scholar Award)',
          parent: 'philanthropy',
        },
        { key: 'star', text: 'STAR Scholarship', parent: 'philanthropy' },
        { key: 'foundation', text: 'Foundation', parent: 'philanthropy' },
        { key: 'cottey', text: 'Cottey College', parent: 'philanthropy' },
        { key: 'donations', text: 'Donations', parent: 'philanthropy' },
      ];

      // Find matching items
      const matches = menuItems.filter((item) =>
        item.text.toLowerCase().includes(query)
      );

      // Get parent sections that need to be expanded
      const parentsToExpand = new Set(
        matches.filter((item) => item.parent).map((item) => item.parent)
      );

      // Expand relevant sections
      if (parentsToExpand.has('chapters-groups')) setOpenChapters(true);
      if (parentsToExpand.has('annual-reports')) setAnnualReportsOpen(true);
      if (parentsToExpand.has('resources')) setResourcesOpen(true);
      if (parentsToExpand.has('shops')) setShopsOpen(true);
      if (parentsToExpand.has('philanthropy')) setProjectsOpen(true);
    };

    window.addEventListener(
      'websiteSearch',
      handleWebsiteSearch as EventListener
    );

    return () => {
      window.removeEventListener(
        'websiteSearch',
        handleWebsiteSearch as EventListener
      );
    };
  }, []);

  const handleRouteSelection = (route: string): void => {
    switch (route) {
      case 'local-chapters':
        navigate('/local-chapters');
        break;
      case 'spd-chapters':
        navigate('/spd-chapters');
        break;
      case 'international':
        navigate('/international');
        break;
      case 'member-search':
        navigate('/user');
        break;
      case 'introductions':
        navigate('/introductions');
        break;
      case 'bnb':
        navigate('/bnb');
        break;
      case 'tlc':
        navigate('/tlc');
        break;
      case 'elf':
        navigate('/projects/elf');
        break;
      case 'ips':
        navigate('/projects/ips');
        break;
      case 'pce':
        navigate('/projects/pce');
        break;
      case 'psa':
        navigate('/projects/psa');
        break;
      case 'star':
        navigate('/projects/star');
        break;
      case 'foundation':
        navigate('/foundation');
        break;
      case 'cottey':
        navigate('/cottey');
        break;
      case 'donations':
        navigate('/internal/donations');
        break;
      case 'messages':
        navigate('/message');
        break;
      case 'marketplace':
        navigate('/marketplace');
        break;
      case 'my-profile':
        navigate(`/user/${localStorage.getItem('user_id')}`);
        break;
      case 'training':
        navigate('/training');
        break;
      case 'leadership-development':
        navigate('/leadership-development');
        break;
      case 'reports':
        if (config.NEW_REPORTS_ENABLED) {
          navigate('/peo-reports');
        } else {
          navigate('/reports');
        }
        break;
      case 'feedback':
        navigate('/feedback');
        break;
      case 'transfer-list':
        navigate('/seeking-transfer');
        break;
      case 'conversations':
        navigate('/conversations');
        break;
      case 'newsfeed':
        navigate('/newsfeed');
        break;
      case 'library':
        navigate('/library');
        break;
      case 'map':
        navigate('/map');
        break;
      case 'ar-dashboard':
        navigate('/annual-reports');
        break;
      case 'ar-prework':
        navigate('/annual-reports/prework');
        break;
      case 'ar-roster-dues':
        navigate('/annual-reports/roster-dues');
        break;
      case 'ar-irs-forms':
        navigate('/annual-reports/irs-forms');
        break;
      case 'ar-resources':
        navigate('/annual-reports/resources');
        break;
      case 'ar-training':
        navigate('/annual-reports/training');
        break;
      case 'administration-permissions':
        navigate('/administration/permissions');
        break;
      case 'administration-executive-office':
        navigate('/administration/executive-office');
        break;
      case 'peo-record':
        // Set preset filters for P.E.O. Record
        localStorage.setItem(
          'documentLibrary_selectedCategories',
          '2d8aa0537657482ea98591494967656b'
        );
        localStorage.setItem(
          'documentLibrary_selectedSubCategories',
          '264b09f6e67a4df9bcda8f61483df2af'
        );
        navigate('/library');
        break;
      default:
        navigate('/');
        break;
    }
  };

  const handleExternalLink = (url: string): void => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleLogOut = (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
    localStorage.removeItem('user_id');
    localStorage.removeItem('first_name');
    localStorage.removeItem('last_name');
    window.location.href = '/login';
  };

  const canUseMessaging = (): boolean => {
    let apps = localStorage.getItem('apps');
    if (!apps) return false;
    let messageUsers = [
      applications.MessageAdmin,
      applications.MessageApprover,
    ];
    return messageUsers.some((app) => apps.includes(app)) ? true : false;
  };

  const canUseDonations = (): boolean => {
    let apps = localStorage.getItem('apps');
    if (!apps) return false;
    let donationsUsers = applications.DonationsViewer;
    return apps.includes(donationsUsers);
  };

  const canUseFeedback = (): boolean => {
    let apps = localStorage.getItem('apps');
    if (!apps) return false;
    let feedbackUsers = applications.FeedbackAdmin;
    return apps.includes(feedbackUsers);
  };

  const canSeekTransfer = (): boolean => {
    return canViewTransferRequests();
  };

  const canUseNewsfeed = (): boolean => {
    let apps = localStorage.getItem('apps');
    if (!apps) return false;
    return apps.includes(applications.NewsfeedAdmin);
  };

  const canUseReports = (): boolean => {
    let role = localStorage.getItem('role');
    let rolesAccepted = [
      roles.Admin,
      roles.Support,
      roles.Communications,
      roles.Executive,
      roles.SPDOfficer,
      roles.Finance,
      roles.RMR,
      roles.LCOfficer,
    ];
    if (rolesAccepted.includes(role)) return true;
    let apps = localStorage.getItem('apps');
    if (!apps) return false;
    if (apps.includes(applications.Reports)) return true;
    return false;
  };

  const canUseAnnualReports = (): boolean => {
    let role = localStorage.getItem('role');
    if (!role) return false;
    let rolesAccepted = [
      roles.Support,
      roles.Finance,
      roles.SPDOfficer,
      roles.RMR,
      roles.Admin,
      roles.LCOfficer,
      roles.Executive,
      roles.Communications,
    ];
    return rolesAccepted.includes(role);
  };

  const canViewPermissions = (): boolean => {
    let pages = localStorage.getItem('pages');
    if (!pages) return false;
    return pages.includes('permissions');
  };

  const canViewExecutiveOffice = (): boolean => {
    let pages = localStorage.getItem('pages');
    if (!pages) return false;
    return pages.includes('exec-office');
  };

  const canViewAdministration = (): boolean => {
    return canViewPermissions() || canViewExecutiveOffice();
  };

  // Helper function to highlight search terms in text
  const highlightText = (text: string, query: string) => {
    if (!query || !text.toLowerCase().includes(query.toLowerCase())) {
      return text;
    }

    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: '#ffeb3b',
            fontWeight: 'bold',
            padding: '1px 2px',
            borderRadius: '2px',
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const DrawerList = (
    <Box sx={{ width: 300 }} role="presentation">
      <Grid container justifyContent="center" alignItems="center">
        <Box
          component="img"
          sx={{
            height: 35,
            width: 74,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
          }}
          alt="Logo."
          src={logo}
          onClick={() => handleRouteSelection('')}
        />
        <Typography
          variant="h6"
          color="primary"
          padding={2}
          fontWeight={'bold'}
        >
          P.E.O. Hub
        </Typography>
        <IconButton onClick={() => handleUpdateDrawer()} color="primary">
          {theme.direction === 'ltr' ? (
            <ChevronLeftIcon />
          ) : (
            <ChevronRightIcon />
          )}
        </IconButton>
      </Grid>
      <Divider sx={{ paddingRight: 2 }} />

      {/* Global Search */}
      <GlobalSearch />

      <Divider sx={{ paddingRight: 2, my: 1 }} />

      <List disablePadding dense={true} sx={{ paddingTop: 0.5 }}>
        <ListItem
          key={'dashboard'}
          disablePadding
          dense={true}
          sx={{ marginBottom: 0.5 }}
        >
          <ListItemButton
            onClick={() => handleRouteSelection('')}
            sx={{ py: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <GridView color="primary" fontSize={'small'} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color="primary" fontWeight={'bold'}>
                  Dashboard
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>

        <ListItem
          key={'chapters-groups'}
          disablePadding
          dense={true}
          sx={{ marginBottom: 0.5 }}
        >
          <ListItemButton
            onClick={() => setOpenChapters(!openChapters)}
            sx={{ py: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <MapIcon color="primary" fontSize={'small'} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color="primary" fontWeight={'bold'}>
                  {highlightText('Chapters / Groups', searchQuery)}
                </Typography>
              }
            />
            {openChapters ? (
              <ExpandLess color="primary" />
            ) : (
              <ExpandMore color="primary" />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={openChapters} timeout="auto" unmountOnExit>
          <ListItem
            disablePadding
            key={'local-chapters'}
            dense={true}
            sx={{ marginBottom: 0.25 }}
          >
            <ListItemButton
              sx={{ pl: 4, py: 0.25 }}
              onClick={() => handleRouteSelection('local-chapters')}
            >
              <ListItemText
                primary={
                  <Typography color="primary" fontSize={14}>
                    {highlightText('Local Chapters', searchQuery)}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem
            disablePadding
            key={'spd-chapters'}
            dense={true}
            sx={{ marginBottom: 0.25 }}
          >
            <ListItemButton
              sx={{ pl: 4, py: 0.25 }}
              onClick={() => handleRouteSelection('spd-chapters')}
            >
              <ListItemText
                primary={
                  <Typography color="primary" fontSize={14}>
                    {highlightText('S/P/D Chapters', searchQuery)}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem
            disablePadding
            key={'reciprocities'}
            dense={true}
            sx={{ marginBottom: 0.25 }}
          >
            <ListItemButton
              sx={{ pl: 4, py: 0.25 }}
              onClick={() => handleRouteSelection('international')}
            >
              <ListItemText
                primary={
                  <Typography color="primary" fontSize={14}>
                    {highlightText('International', searchQuery)}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem
            disablePadding
            key={'member-search'}
            dense={true}
            sx={{ marginBottom: 0.25 }}
          >
            <ListItemButton
              sx={{ pl: 4, py: 0.25 }}
              onClick={() => handleRouteSelection('member-search')}
            >
              <ListItemText
                primary={
                  <Typography color="primary" fontSize={14}>
                    {highlightText('Member Search', searchQuery)}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem
            disablePadding
            key={'introductions'}
            dense={true}
            sx={{ marginBottom: 0.25 }}
          >
            <ListItemButton
              sx={{ pl: 4, py: 0.25 }}
              onClick={() => handleRouteSelection('introductions')}
            >
              <ListItemText
                primary={
                  <Typography color="primary" fontSize={14}>
                    {highlightText('Non-Member Intro', searchQuery)}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        </Collapse>
        {canUseAnnualReports() && (
          <ListItem
            key={'annual-reports'}
            disablePadding
            dense={true}
            sx={{ marginBottom: 0.5 }}
          >
            <ListItemButton
              onClick={() => setAnnualReportsOpen(!annualReportsOpen)}
              sx={{ py: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AssessmentIcon color="primary" fontSize={'small'} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography color="primary" fontWeight={'bold'}>
                    {highlightText('Annual Reports', searchQuery)}
                  </Typography>
                }
              />
              {annualReportsOpen ? (
                <ExpandLess color="primary" />
              ) : (
                <ExpandMore color="primary" />
              )}
            </ListItemButton>
          </ListItem>
        )}
        {canUseAnnualReports() && (
          <Collapse in={annualReportsOpen} timeout="auto" unmountOnExit>
            <List disablePadding dense={true}>
              <ListItem
                disablePadding
                key={'ar-dashboard'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('ar-dashboard')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('Annual Reports Dashboard', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
              <ListItem
                disablePadding
                key={'ar-prework'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('ar-prework')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('Prework', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
              <ListItem
                disablePadding
                key={'ar-roster-dues'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('ar-roster-dues')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('Roster & Dues', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
              <ListItem
                disablePadding
                key={'ar-irs-forms'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('ar-irs-forms')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('IRS Forms', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
              <ListItem
                disablePadding
                key={'ar-resources'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('ar-resources')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('Resources', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
              <ListItem
                disablePadding
                key={'ar-training'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('ar-training')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('Training', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Collapse>
        )}
        {canViewAdministration() && (
          <>
            <ListItem
              key={'administration'}
              disablePadding
              dense={true}
              sx={{ marginBottom: 0.5 }}
            >
              <ListItemButton
                onClick={() => setAdministrationOpen(!administrationOpen)}
                sx={{ py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <AdminPanelSettings color="primary" fontSize={'small'} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography color="primary" fontWeight={'bold'}>
                      {highlightText('Administration', searchQuery)}
                    </Typography>
                  }
                />
                {administrationOpen ? (
                  <ExpandLess color="primary" />
                ) : (
                  <ExpandMore color="primary" />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={administrationOpen} timeout="auto" unmountOnExit>
              <List disablePadding dense={true}>
                {canViewPermissions() && (
                  <ListItem
                    disablePadding
                    key={'administration-permissions'}
                    dense={true}
                    sx={{ marginBottom: 0.25 }}
                  >
                    <ListItemButton
                      sx={{ pl: 4, py: 0.25 }}
                      onClick={() =>
                        handleRouteSelection('administration-permissions')
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography color="primary" fontSize={14}>
                            {highlightText('Permissions', searchQuery)}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                )}
                {canViewExecutiveOffice() && (
                  <ListItem
                    disablePadding
                    key={'administration-executive-office'}
                    dense={true}
                    sx={{ marginBottom: 0.25 }}
                  >
                    <ListItemButton
                      sx={{ pl: 4, py: 0.25 }}
                      onClick={() =>
                        handleRouteSelection('administration-executive-office')
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography color="primary" fontSize={14}>
                            {highlightText('Executive Office', searchQuery)}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Collapse>
          </>
        )}
        <ListItem
          key={'resources'}
          disablePadding
          dense={true}
          sx={{ marginBottom: 0.5 }}
        >
          <ListItemButton
            onClick={() => setResourcesOpen(!resourcesOpen)}
            sx={{ py: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <PeopleIcon color="primary" fontSize={'small'} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color="primary" fontWeight={'bold'}>
                  {highlightText('Resources', searchQuery)}
                </Typography>
              }
            />
            {resourcesOpen ? (
              <ExpandLess color="primary" />
            ) : (
              <ExpandMore color="primary" />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={resourcesOpen} timeout="auto" unmountOnExit>
          <List disablePadding dense={true}>
            <ListItem
              disablePadding
              key={'peo-record'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('peo-record')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('P.E.O. Record', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'bnb'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('bnb')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('Bed and Breakfast', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'tlc'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('tlc')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('TLC', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            {canSeekTransfer() && (
              <ListItem
                disablePadding
                key={'transfer-list'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('transfer-list')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('Transfer List', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem
              disablePadding
              key={'library'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('library')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('Library', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'map'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('map')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('P.E.O. Map', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>

            <ListItem
              disablePadding
              key={'training'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('training')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('Training', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'leadership-development'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('leadership-development')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('Leadership Development', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            {canUseNewsfeed() && (
              <ListItem
                disablePadding
                key={'newsfeed'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('newsfeed')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('Newsfeed', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
            {canUseReports() && (
              <ListItem
                disablePadding
                key={'reports'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('reports')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('Reports', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
            {canUseMessaging() && (
              <ListItem
                disablePadding
                key={'messages'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('messages')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('Messaging', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
            {canUseFeedback() && (
              <ListItem
                disablePadding
                key={'feedback'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('feedback')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('Feedback', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Collapse>
        <ListItem
          key={'shops'}
          disablePadding
          dense={true}
          sx={{ marginBottom: 0.5 }}
        >
          <ListItemButton
            onClick={() => setShopsOpen(!shopsOpen)}
            sx={{ py: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ShoppingCart color="primary" fontSize={'small'} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color="primary" fontWeight={'bold'}>
                  {highlightText('Shops', searchQuery)}
                </Typography>
              }
            />
            {shopsOpen ? (
              <ExpandLess color="primary" />
            ) : (
              <ExpandMore color="primary" />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={shopsOpen} timeout="auto" unmountOnExit>
          <List disablePadding dense={true}>
            <ListItem
              disablePadding
              key={'marketplace'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('marketplace')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('Fundraising Marketplace', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'jewelry'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() =>
                  handleExternalLink(
                    'https://www.hjgreek.com/index.cfm?event=Products.showCategories&Organization_ID=56'
                  )
                }
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('Jewelry (Herff Jones)', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'chapter-supplies'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() =>
                  handleExternalLink('https://eorders.peointernational.org/')
                }
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('Chapter Supplies', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'emblem-order'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() =>
                  handleExternalLink('https://eorders.peointernational.org/')
                }
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('Emblem Order', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>
        <ListItem
          key={'philanthropy'}
          disablePadding
          dense={true}
          sx={{ marginBottom: 0.5 }}
        >
          <ListItemButton
            onClick={() => setProjectsOpen(!projectsOpen)}
            sx={{ py: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AccountBalanceIcon color="primary" fontSize={'small'} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color="primary" fontWeight={'bold'}>
                  {highlightText('Projects / Foundation', searchQuery)}
                </Typography>
              }
            />
            {projectsOpen ? (
              <ExpandLess color="primary" />
            ) : (
              <ExpandMore color="primary" />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={projectsOpen} timeout="auto" unmountOnExit>
          <List disablePadding dense={true}>
            <ListItem
              disablePadding
              key={'elf'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('elf')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText(
                        'ELF (Educational Loan Fund)',
                        searchQuery
                      )}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'ips'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('ips')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText(
                        'IPS (International Peace Scholarship)',
                        searchQuery
                      )}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>

            <ListItem
              disablePadding
              key={'pce'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('pce')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText(
                        'PCE (Program for Continuing Education)',
                        searchQuery
                      )}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'psa'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('psa')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('PSA (P.E.O. Scholar Award)', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'star'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('star')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('STAR Scholarship', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'foundation'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('foundation')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('Foundation', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              key={'cottey'}
              dense={true}
              sx={{ marginBottom: 0.25 }}
            >
              <ListItemButton
                sx={{ pl: 4, py: 0.25 }}
                onClick={() => handleRouteSelection('cottey')}
              >
                <ListItemText
                  primary={
                    <Typography color="primary" fontSize={14}>
                      {highlightText('Cottey College', searchQuery)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>

            {canUseDonations() && (
              <ListItem
                disablePadding
                key={'donations'}
                dense={true}
                sx={{ marginBottom: 0.25 }}
              >
                <ListItemButton
                  sx={{ pl: 4, py: 0.25 }}
                  onClick={() => handleRouteSelection('donations')}
                >
                  <ListItemText
                    primary={
                      <Typography color="primary" fontSize={14}>
                        {highlightText('Donations', searchQuery)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Collapse>
        <ListItem
          key={'conversations'}
          disablePadding
          dense={true}
          sx={{ marginBottom: 0.5 }}
        >
          <ListItemButton
            onClick={() => handleRouteSelection('conversations')}
            sx={{ py: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Message color="primary" fontSize={'small'} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color="primary" fontWeight={'bold'}>
                  {unreadCountData &&
                  typeof unreadCountData === 'object' &&
                  'count' in unreadCountData &&
                  unreadCountData.count > 0
                    ? `My Messages (${unreadCountData.count})`
                    : 'My Messages'}
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>
        <ListItem
          key={'profile'}
          disablePadding
          dense={true}
          sx={{ marginBottom: 0.5 }}
        >
          <ListItemButton
            onClick={() => handleRouteSelection('my-profile')}
            sx={{ py: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AccountCircleIcon color="primary" fontSize={'small'} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color="primary" fontWeight={'bold'}>
                  My Profile
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>
        <ListItem
          key={'logout'}
          disablePadding
          dense={true}
          sx={{ marginBottom: 0.5 }}
        >
          <ListItemButton onClick={handleLogOut} sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LogoutIcon color="primary" fontSize={'small'} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color="primary" fontWeight={'bold'}>
                  Logout
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
  // Here we need to detect if the screen is mobile or not so that we can have
  // a different drawer for mobile and desktop
  // For now we will just use the same drawer for both
  return (
    <React.Fragment>
      {!open && (
        <IconButton
          onClick={() => handleUpdateDrawer()}
          sx={{ color: 'primary.main' }}
          size="large"
        >
          <Menu />
        </IconButton>
      )}

      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor={'left'}
        open={open}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {DrawerList}
      </Drawer>
    </React.Fragment>
  );
}
